"use client";

import Link from "next/link";
import { MapPin, Clock } from "lucide-react";
import { Item } from "@/lib/api/products";
import { generateProductImageAlt } from "@/lib/seo/generateImageAlt";

interface RecommendedItemsProps {
  items: Item[];
  title?: string;
  showMoreLink?: boolean;
  onItemClick?: (item: Item) => void;
}

/**
 * 유사 매물 추천 섹션 컴포넌트
 * - 2열 카드 그리드
 * - 이미지 정사각형 비율
 * - 제목 / 브랜드 / 가격 강조
 * - 톤마켓 톤앤매너 (화이트 + 보라 #6C4DE6 기반)
 * - 모바일 최적화
 */
export function RecommendedItems({
  items,
  title = "유사한 매물 추천",
  showMoreLink = false,
  onItemClick,
}: RecommendedItemsProps) {
  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat("ko-KR").format(price);
  };

  const getTimeAgo = (createdAt: any): string => {
    if (!createdAt) return "";
    try {
      const dateObj =
        createdAt.seconds !== undefined
          ? new Date(createdAt.seconds * 1000)
          : new Date(createdAt);
      const now = new Date();
      const diffInMs = now.getTime() - dateObj.getTime();
      const diffInMinutes = Math.floor(diffInMs / 60000);
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);

      if (diffInMinutes < 1) return "방금 전";
      else if (diffInMinutes < 60) return `${diffInMinutes}분 전`;
      else if (diffInHours < 24) return `${diffInHours}시간 전`;
      else if (diffInDays < 7) return `${diffInDays}일 전`;
      else return dateObj.toLocaleDateString("ko-KR");
    } catch (error) {
      return "";
    }
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        {showMoreLink && (
          <Link
            href="/list"
            className="text-sm text-[#6C4DE6] hover:text-[#5A3FE8] font-medium transition-colors"
          >
            더보기 →
          </Link>
        )}
      </div>

      {/* 2열 그리드 */}
      <div className="grid grid-cols-2 gap-4">
        {items.map((item) => {
          const itemTitle =
            (item as any).title ||
            `${(item as any).brand || ""} ${(item as any).model || ""}`.trim() ||
            "악기";
          const image = item.images?.[0] || "";
          const brand = (item as any).brand || "";

          return (
            <Link
              key={item.id}
              href={`/product/${item.id}`}
              onClick={() => onItemClick?.(item)}
              className="group"
            >
              <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#6C4DE6]/30 hover:shadow-md transition-all duration-200 hover:-translate-y-1">
                {/* 이미지 (정사각형 비율) */}
                <div className="relative aspect-square bg-gray-100 overflow-hidden">
                  {image ? (
                    <img
                      src={image}
                      alt={generateProductImageAlt(item as any, 0)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                      loading="lazy"
                      onError={(e) => {
                        // 이미지 로드 실패 시 플레이스홀더 표시
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const placeholder = target.parentElement?.querySelector('.image-placeholder') as HTMLElement;
                        if (placeholder) {
                          placeholder.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br from-[#F4F0FF] to-[#E8E0FF] image-placeholder ${image ? 'hidden' : ''}`}>
                    <span className="text-4xl">🎵</span>
                  </div>
                </div>

                {/* 정보 */}
                <div className="p-3 space-y-2">
                  {/* 브랜드 */}
                  {brand && (
                    <div className="text-xs font-medium text-[#6C4DE6]">
                      {brand}
                    </div>
                  )}

                  {/* 제목 */}
                  <h3 className="font-semibold text-gray-900 text-sm line-clamp-2 leading-tight group-hover:text-[#6C4DE6] transition-colors">
                    {itemTitle}
                  </h3>

                  {/* 가격 */}
                  <div className="text-base font-bold text-[#6C4DE6]">
                    {formatPrice(item.price)}원
                  </div>

                  {/* 메타 정보 */}
                  <div className="flex items-center gap-3 text-xs text-gray-500 pt-1">
                    {item.region && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{item.region}</span>
                      </div>
                    )}
                    {item.createdAt && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{getTimeAgo(item.createdAt)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/**
 * 로딩 스켈레톤 컴포넌트
 */
export function RecommendedItemsSkeleton() {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <div className="h-7 bg-gray-200 rounded-lg w-40 mb-5 animate-pulse" />
      <div className="grid grid-cols-2 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="space-y-3">
            <div className="aspect-square bg-gray-200 rounded-2xl animate-pulse" />
            <div className="space-y-2">
              <div className="h-3 bg-gray-200 rounded w-16 animate-pulse" />
              <div className="h-4 bg-gray-200 rounded w-full animate-pulse" />
              <div className="h-5 bg-gray-200 rounded w-24 animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * 빈 상태 컴포넌트
 */
export function RecommendedItemsEmpty() {
  return (
    <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
      <div className="text-4xl mb-3">🎵</div>
      <p className="text-gray-500 text-sm">
        아직 유사한 매물이 없습니다
      </p>
    </div>
  );
}

